import React, { useRef, useState, useEffect, useCallback } from 'react';
import type { ToolType, Position } from '../schemas';
import { useDrag } from './hooks/useDrag';
import { useRotate } from './hooks/useRotate';
import { useJiggle } from '../hooks/useJiggle';
import { RulerSvg, ProtractorSvg, SetSquareSvg } from './geometry-svgs';

export interface DraggableToolProps {
  id: string;
  type: ToolType;
  /** Only used when type === 'custom'. Geometry tools render inline SVG. */
  imageSrc?: string;
  initialPosition?: Position;
  initialRotation?: number;
  width?: number;
  className?: string;
}

/** Left/right edges rotate; the center drags. */
const EXTREMITY_RATIO = 0.25;

interface ToolContentProps {
  type: ToolType;
  width?: number;
  imageSrc?: string;
  /** Mirrors the set-square geometry, not the wrapper. */
  flipped?: boolean;
}

const ToolContent: React.FC<ToolContentProps> = ({ type, width, imageSrc, flipped }) => {
  switch (type) {
    case 'ruler':
      return <RulerSvg width={width} />;
    case 'protractor':
      return <ProtractorSvg width={width} />;
    case 'set-square':
      return <SetSquareSvg width={width} flipped={flipped} />;
    default:
      return imageSrc ? (
        <img
          src={imageSrc}
          alt={`${type} tool`}
          draggable={false}
          style={{ width: width ? `${width}px` : 'auto', pointerEvents: 'none', display: 'block' }}
        />
      ) : null;
  }
};

/**
 * Draggable geometry tool.
 * Center drag moves it; edge drag and mouse wheel rotate it.
 */
export const DraggableTool: React.FC<DraggableToolProps> = ({
  id,
  type,
  imageSrc,
  initialPosition = { x: 0, y: 0 },
  initialRotation = 0,
  width,
  className = '',
}) => {
  const toolRef = useRef<HTMLDivElement>(null);
  const modeRef = useRef<'drag' | 'rotate' | null>(null);
  const touchRotateCentre = useRef<{ x: number; y: number } | null>(null);

  const [inRotateZone, setInRotateZone] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  const drag = useDrag({ initialPosition });
  const rotate = useRotate({ initialRotation });
  const { jiggling, onAnimationEnd } = useJiggle(drag.isDragging);

  // Native listeners need stable refs to the latest hook callbacks.
  const dragRef = useRef(drag);
  const rotateRef = useRef(rotate);
  dragRef.current = drag;
  rotateRef.current = rotate;

  const isInExtremity = useCallback((clientX: number): boolean => {
    const rect = toolRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return false;
    const ratio = (clientX - rect.left) / rect.width;
    return ratio < EXTREMITY_RATIO || ratio > 1 - EXTREMITY_RATIO;
  }, []);

  useEffect(() => {
    const el = toolRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if ((e.target as Element).closest('.tool-flip-btn')) return;
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;

      if (isInExtremity(touch.clientX)) {
        modeRef.current = 'rotate';
        const rect = el.getBoundingClientRect();
        touchRotateCentre.current = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
      } else {
        modeRef.current = 'drag';
        dragRef.current.startDrag(touch.clientX, touch.clientY);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;

      if (modeRef.current === 'rotate' && touchRotateCentre.current) {
        const { x: cx, y: cy } = touchRotateCentre.current;
        const angle = Math.atan2(touch.clientY - cy, touch.clientX - cx) * (180 / Math.PI);
        rotateRef.current.setRotation(angle);
      } else if (modeRef.current === 'drag') {
        dragRef.current.moveDrag(touch.clientX, touch.clientY);
      }
    };

    const onTouchEnd = () => {
      if (modeRef.current === 'drag') dragRef.current.endDrag();
      touchRotateCentre.current = null;
      modeRef.current = null;
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 5 : -5;
      rotateRef.current.setRotation((prev: number) => ((prev + delta) % 360 + 360) % 360);
    };

    const opts: AddEventListenerOptions = { passive: false };
    el.addEventListener('touchstart', onTouchStart, opts);
    el.addEventListener('touchmove', onTouchMove, opts);
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    el.addEventListener('wheel', onWheel, opts);

    return () => {
      el.removeEventListener('touchstart', onTouchStart, opts);
      el.removeEventListener('touchmove', onTouchMove, opts);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('wheel', onWheel, opts);
    };
  }, [isInExtremity]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isInExtremity(e.clientX)) {
      modeRef.current = 'rotate';

      const rect = toolRef.current!.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      const onMove = (me: MouseEvent) => {
        rotateRef.current.setRotation(
          Math.atan2(me.clientY - cy, me.clientX - cx) * (180 / Math.PI)
        );
      };
      const onUp = () => {
        modeRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    } else {
      modeRef.current = 'drag';
      dragRef.current.startDrag(e.clientX, e.clientY);

      const onMove = (me: MouseEvent) => {
        dragRef.current.moveDrag(me.clientX, me.clientY);
      };
      const onUp = () => {
        dragRef.current.endDrag();
        modeRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setInRotateZone(isInExtremity(e.clientX));
  };

  const handleMouseLeave = () => {
    setInRotateZone(false);
  };

  const cursor = drag.isDragging ? 'grabbing' : inRotateZone ? 'crosshair' : 'grab';

  return (
    <div
      ref={toolRef}
      id={id}
      className={[
        'draggable-tool',
        `draggable-tool-${type}`,
        drag.isDragging ? 'dragging' : '',
        className,
      ].filter(Boolean).join(' ')}
      style={{
        position: 'absolute',
        left: drag.position.x,
        top: drag.position.y,
        transform: `rotate(${rotate.rotation}deg)`,
        cursor,
        zIndex: drag.isDragging ? 1000 : 100,
        userSelect: 'none',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={jiggling ? 'tool-content-jiggle' : undefined}
        onAnimationEnd={onAnimationEnd}
        style={{ pointerEvents: 'none', display: 'block' }}
      >
        <ToolContent type={type} width={width} imageSrc={imageSrc} flipped={isFlipped} />
      </div>
      {type === 'set-square' && (
        <button
          className={`tool-flip-btn${isFlipped ? ' tool-flip-btn--active' : ''}`}
          title="Retourner l'équerre"
          aria-label="Retourner l'équerre"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setIsFlipped(f => !f)}
        >
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <polygon points="3,17 9,17 9,7" fill="currentColor" />
            <line x1="10" y1="2" x2="10" y2="18"
              stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 1.5" strokeLinecap="round" />
            <polygon points="17,17 11,17 11,7" fill="currentColor" opacity="0.4" />
          </svg>
        </button>
      )}
    </div>
  );
};
